import { TabNav, TabContent } from "components/layouts/TabComponent";
import Button from "components/form/Button";
import React, { useEffect, useState } from "react";
// import Deposit from './Deposit';
import Transaction from "./Transaction";
import clsx from "clsx";
import SelectInput from "components/form/SelectInput";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { NumberInput } from "components/form/NumberInput";
import { CryptoChart } from "components/dashboard/Chart";
import { coins } from "utils/coins";
import { DepositSchema, DepositSchemaType } from "pages/investment/Investment";
import ExchangeTab from "./ExchangeTab";
import useAccountStore from "state/accountStore";
import { NumberDisplay } from "components/layouts/Display";
import { getTransactions } from "api/transaction";
import { toast } from "react-toastify";
import useUserStore from "state/userStore";
import { Link } from "react-router-dom";
import InfoCard from "components/dashboard/Info";
// import Withdraw from './Withdraw';

const TrxComponent = () => {
  const {
    control,
    formState: { isDirty, isValid },
  } = useForm<DepositSchemaType>({
    resolver: zodResolver(DepositSchema),
    mode: "onTouched",
    defaultValues: {
      coin: "bitcoin",
    },
  });
  const [openModal, setOpenModal] = useState(false);
  const [currentTab, setCurrentTab] = useState("tab1");
  const [cryptoIcon, setCryptoIcon] = useState("/assets/icons/bitcoin_two.svg");

  const handleIconChange = (value: string) => {
    switch (value) {
      case "bitcoin":
        setCryptoIcon("/assets/icons/bitcoin_two.svg");
        break;
      case "usdt":
        setCryptoIcon("/assets/icons/usdt.svg");
        break;
      case "ethereum":
        setCryptoIcon("/assets/icons/ethereum.svg");
        break;
      case "bnb":
        setCryptoIcon("/assets/icons/bnb.svg");
        break;
      default:
        setCryptoIcon("/assets/icons/bitcoin_two.svg");
        break;
    }
  };

  // const handleOpenModal = () => {
  //   setOpenModal(true);
  // };

  return (
    <div className="space-y-3">
      <div className="flex gap-x-4">
        <TabNav
          title="Deposit"
          active={currentTab === "tab1"}
          setCurrentTab={setCurrentTab}
          tabId="tab1"
          className={clsx(
            "!px-4",
            currentTab === "tab1"
              ? "!bg-brand-dark !text-white"
              : "border border-black",
          )}
        />
        <TabNav
          title="Exchange"
          active={currentTab === "tab2"}
          handleClick={() => {}}
          tabId="tab2"
          className={clsx(
            "!px-4",
            currentTab === "tab2"
              ? "!bg-brand-dark !text-white"
              : "border border-black",
          )}
        />
      </div>
      <TabContent currentTab={currentTab} tabId="tab1" className="py-4">
        <form className="flex flex-col gap-y-8">
          <div className="flex flex-col gap-y-2">
            <label htmlFor="coin" className="font-semibold">
              Coin:
            </label>

            <SelectInput
              control={control}
              options={coins}
              name="coin"
              containerClass="bg-white !py-3 md:!py-1"
              startIcon={
                <img src={cryptoIcon} alt="coin_icon" className="h-6 w-6" />
              }
              handleOnChange={handleIconChange}
            />
          </div>
          <div className="flex flex-col gap-y-2">
            <label htmlFor="coin" className="font-semibold">
              Amount:
            </label>

            <NumberInput
              control={control}
              name="amount"
              placeholder="$12,000"
              prefix={"$"}
              decimalScale={2}
              fixedDecimalScale
              className="py-3 md:py-1"
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-lg font-semibold text-gray-500">Total:</p>
            <p>{useWatch({ control, name: "amount" }) ?? "$0"}</p>
          </div>
          <Button disabled={!isValid || !isDirty}>
            <Link
              className="flex w-full items-center justify-center gap-x-3 rounded-md bg-brand-dark !py-2 text-white"
              to="invest"
            >
              <img src={cryptoIcon} alt="crypto_icon" className="h-5 w-5" />
              <span>Buy {useWatch({ control, name: "coin" })}</span>
            </Link>
          </Button>
        </form>
      </TabContent>
      {openModal && <ExchangeTab toggleModal={() => setOpenModal(false)} />}
    </div>
  );
};

const Account = () => {
  const [currentTab, setCurrentTab] = useState("tab1");
  const [transactionsData, setTransactionsData] = useState([]);
  const { account } = useAccountStore();
  const { user } = useUserStore();

  const fetchTransactions = async () => {
    try {
      const res = await getTransactions();
      if (res?.data) {
        const data = res.data.map((item: any, index: number) => {
          return {
            sn: index + 1,
            ...item,
          };
        });
        setTransactionsData(data);
      }
    } catch (error: any) {
      const { errors } = error.response.data;

      errors.map((err: { error: string }) => {
        if (err?.error) {
          return toast.error(err.error);
        } else {
          return toast.error("Something went wrong");
        }
      });
      console.log(error);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  return (
    <section className="py-4">
      <div className="flex flex-col justify-between gap-10 md:flex-row">
        <section className="basis-full md:basis-[73%]">
          <div className="grid grid-cols-1 gap-y-5">
            <InfoCard
              title="Information / Announcement"
              level={user?.planType ?? "Premium"}
              {...(user?.message ? { message: user?.message } : {})}
              {...(user?.announcement
                ? { announcement: user.announcement }
                : {})}
            />
            <section className="my-4 flex gap-x-8 overflow-x-auto pb-3">
              <div className="w-full rounded-md border border-green-400 bg-green-300 px-5 py-8 text-center md:w-3/5">
                <p className="text-gray-700">Balance</p>
                <p className="font-bold">
                  <NumberDisplay value={account?.balance ?? 0} />
                </p>
              </div>
              <div className="w-full rounded-md border border-blue-400 bg-[#8bb9e3] px-5 py-8 text-center md:w-3/5">
                <p className="text-gray-700">Investment</p>
                <p className="font-bold">
                  <NumberDisplay value={account?.investment ?? 0} />
                </p>
              </div>
              <div className="w-full rounded-md border bg-brand-dark px-5 py-8 text-center text-white md:w-3/5">
                <p>Profit</p>
                <p className="font-bold">
                  <NumberDisplay value={account?.profit ?? 0} />
                </p>
              </div>
            </section>
            <section className="h-full">
              <div className="flex">
                <TabNav
                  title="Rate Chart"
                  active={currentTab === "tab1"}
                  setCurrentTab={setCurrentTab}
                  tabId="tab1"
                />
                <TabNav
                  title="Transactions"
                  active={currentTab === "tab2"}
                  setCurrentTab={setCurrentTab}
                  tabId="tab2"
                />
              </div>
              <TabContent
                currentTab={currentTab}
                tabId="tab1"
                className="bg-gree h-full py-2"
              >
                <CryptoChart />
              </TabContent>
              <TabContent currentTab={currentTab} tabId="tab2">
                <Transaction
                  data={transactionsData}
                  size={4}
                  containerClass="px-3"
                />
              </TabContent>
            </section>
          </div>
        </section>
        <section className="basis-full md:basis-[27%]">
          <div className="flex flex-col gap-y-10">
            <div className="rounded-md bg-[#eff1f2] p-5 text-center">
              <div className="mx-auto h-[100px] w-[100px] rounded-full p-1">
                <img
                  src={user?.profileImage}
                  alt="profile_image"
                  className="h-full w-full rounded-full object-cover"
                />
              </div>

              <div className="my-4">
                <p className="text-[15px] text-gray-700">Total Balance</p>
                <br />
                <div className="text-lg font-bold">
                  <NumberDisplay value={account?.balance ?? 0} />
                </div>
              </div>
              <div className="my-4 mx-auto h-8 w-8 rounded-full bg-green-400 p-[6px]">
                <span className="m-auto">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                </span>
              </div>
            </div>
            <div className="rounded-md bg-[#eff1f2] p-5">
              <TrxComponent />
            </div>
          </div>
        </section>
      </div>
      {/* <Withdraw toggleModal={() => console.log()} /> */}
    </section>
  );
};

export default Account;
